// retryToast — small UX helper that turns the `onRetry` callback from
// `retryWithBackoff` into a live, ticking sonner toast.
//
// Why a helper: every fork/publish call site was reaching for the same
// `toast.loading("Retrying (n/3)…", { id })` snippet, but none of them
// surfaced the *backoff delay* — which can be up to 4 seconds with the
// default settings. The user sees "Retrying…" and has no idea whether
// the next attempt fires in 200ms or 4s, so the flow feels stuck.
//
// This helper renders a single sticky toast that:
//   • shows attempt N / max
//   • counts down the remaining backoff in 0.1s ticks
//   • renders a textual progress bar so the wait is visible at a glance
//
// It returns an `onRetry` function ready to drop into `retryWithBackoff`
// options, plus a `dismiss()` to clean up after success/failure.
import { toast } from "sonner";

const BAR_WIDTH = 16;

const renderBar = (remainingMs: number, totalMs: number): string => {
  if (totalMs <= 0) return "";
  const pct = Math.max(0, Math.min(1, 1 - remainingMs / totalMs));
  const filled = Math.round(pct * BAR_WIDTH);
  return `[${"█".repeat(filled)}${"·".repeat(BAR_WIDTH - filled)}]`;
};

export interface RetryToastHandle {
  /** Pass directly to `retryWithBackoff({ onRetry })`. */
  onRetry: (info: { attempt: number; nextAttempt: number; delayMs: number }) => void;
  /** Call when the wrapped operation succeeds OR throws — clears the
   *  countdown interval and dismisses the toast. Safe to call twice. */
  dismiss: () => void;
}

interface CreateOptions {
  /** Stable id so successive retry pings replace each other. */
  id: string;
  /** Verb the user sees, e.g. "Snapshot", "Publish", "Fork". */
  label: string;
  /** Total attempts that will be made (matches retry config). */
  maxAttempts?: number;
}

/** Build a retry toast handle. The actual toast appears on the FIRST
 *  retry; success paths that never call `onRetry` stay silent. */
export const createRetryToast = ({
  id,
  label,
  maxAttempts = 3,
}: CreateOptions): RetryToastHandle => {
  let interval: ReturnType<typeof setInterval> | null = null;

  const clearTimer = () => {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
  };

  const onRetry: RetryToastHandle["onRetry"] = ({ nextAttempt, delayMs }) => {
    clearTimer();
    const startedAt = Date.now();
    const total = Math.max(1, delayMs);

    const render = () => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, total - elapsed);
      const seconds = (remaining / 1000).toFixed(1);
      toast.loading(
        `${label} hiccup — retry ${nextAttempt}/${maxAttempts} in ${seconds}s`,
        {
          id,
          description: `${renderBar(remaining, total)} waiting before next attempt…`,
        },
      );
      if (remaining <= 0) clearTimer();
    };

    render();
    interval = setInterval(render, 100);
  };

  const dismiss = () => {
    clearTimer();
    toast.dismiss(id);
  };

  return { onRetry, dismiss };
};